import { formatDate } from '@angular/common';
import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppSettings } from 'pac-api';
import { Observable, of } from 'rxjs';
import { map, shareReplay, switchMap, tap, delay } from 'rxjs/operators';

import { AccountTrainingService } from '../../account-training.service';
import { AlertModalComponent } from '../alert-modal/alert-modal.component';
import { AppComponent } from '../../app.component';
import { MembershipSelectionService } from '../../association-level/membership-selection.service';
import { CartService } from '../../cart.service';
import { CPP, empty, MbrAsn } from '../../helpers';
import { AsnLicenseType, AsnLogo, AsnName, Association, Associations } from '../../model/assoication';
import { ITrainingRecord, SkeletonActions, TrainingRecord } from '../../model/training-record';
import { WizardMembershipSelection } from '../../model/wizard.model';
import { WizardService } from '../../wizard/wizard.service';
import { PopupService } from '../popup.service';

@Component({
  selector: 'app-training',
  templateUrl: './training.component.html',
  styleUrls: ['./training.component.css']
})
export class TrainingComponent implements OnInit, OnDestroy, AfterViewInit {

  private _group: UntypedFormGroup = null;

  @Input()
  public set group(group: UntypedFormGroup) {
    if (group) {
      if (this.form) {
        group.registerControl('Training', this.form);
      }
    } else if (this._group) {
      if ('Training' in this._group.controls) {
        this._group.removeControl('Training');
      }
    }

    this._group = group;
  }

  public get group(): UntypedFormGroup {
    return this._group;
  }

  @Input()
  public Skeleton: ITrainingRecord = null;

  @Input()
  public SkeletonActions: SkeletonActions = null;

  public form: UntypedFormGroup;

  public Title: string;

  public Logo: URL;

  public Type: string;

  public Asn: Association = undefined;

  public Level: 'Student' | 'Professional' | 'Associate' | 'Graduate' = 'Professional';

  public Training$: Observable<TrainingRecord> = undefined;

  public Selection: WizardMembershipSelection;

  public PreviousRecord: TrainingRecord = null;

  public get valid(): boolean {
    return (this.group) ? this.group.valid : this.form.valid;
  }

  public get invalid(): boolean {
    return (this.group) ? this.group.invalid : this.form.invalid;
  }

  constructor(
    private wizard: WizardService,
    private membership: MembershipSelectionService,
    private service: AccountTrainingService,
    private cart: CartService,
    private app: AppComponent,
    private route: ActivatedRoute,
    private router: Router,
    private settings: AppSettings,
    private popup: PopupService,
  ) {
  }

  public ngOnInit(): void {
    this.route.paramMap.pipe(
      delay(0),
    ).subscribe(params => {
      if (params.has('asn')) {
        this.Asn = params.get('asn') as Association;
        this.Title = AsnName[this.Asn];
        this.Logo = new URL(AsnLogo[this.Asn]);
        this.Type = AsnLicenseType[this.Asn];
        this.PreviousRecord = null;

        const graduationDateValidators = [Validators.required, WizardService.PlausibleDate, WizardService.ValidDate];
        const courseLengthValidators = [Validators.required];

        this.Selection = this.service.GetMembershipSelection(this.Asn);

        if (this.Selection) {
          if (this.Selection.Type && this.Selection.Type.mbt_code === 'Associate') {
            this.Level = 'Associate';
          } else {
            this.Level = this.Selection.Level;
          }
        }

        if (this.Level === 'Associate') {
          this.service.Training.set(this.Asn, null);
          this.Next();
        } else if (this.Level === 'Professional') {
          // Past date validation is just a warning
        } else if (this.Level === 'Student') {
          if (!this.membership.Data.Memberships[this.Asn].Skip) {
            graduationDateValidators.push(WizardService.FutureDate);
          }
        }

        this.form = new UntypedFormGroup({
          Key: new UntypedFormControl(null, [Validators.required]),
          Association: new UntypedFormControl(null, [Validators.required]),
          CourseLength: new UntypedFormControl(null, courseLengthValidators),
          GraduationDate: new UntypedFormControl(null, graduationDateValidators),
          NameAttended: new UntypedFormControl(null, [Validators.required])
        });

        this.form.reset({});

        if (this.group) {
          this.group.registerControl('Training', this.form);
        }

        this.form.get('Association').reset(this.Asn);

        // Get Previous training record
        for (const asn of Associations) {
          if (asn === this.Asn) {
            break;
          }

          if (this.wizard.Package[asn] && this.service.Training.has(asn)) {
            this.PreviousRecord = this.service.Training.get(asn);
            break;
          }
        }

        this.Training$ = this.GetTraining().pipe(
          delay(0),
          tap(training => {
          if (this.CanSkip(training)) {
            this.Next();
          } else {
          }
        }));
      } else {
        if (this.wizard.Package) {
          for (const asn of Associations) {
            if (this.wizard.Package[asn] || this.membership.Data.Associations[asn]) {
              this.service.GetTrainingRecord(asn).subscribe(training => {
                this.router.navigate([asn], { relativeTo: this.route });
              });
              break;
            }
          }
        } else if (this.wizard.isAffinity) {
          this.router.navigate(['/signup/AffinityPortal', this.wizard.Affinity, 'select']);
        } else {
          this.router.navigate(['/signup', 'selection']);
        }
      }
    });
  }

  public ngAfterViewInit(): void {
    if (!this.wizard.Package) {
      const msg = 'Could not find package information. Please start the signup process from the beginning.';
      const popup: AlertModalComponent = this.popup.Open(msg, { CloseText: 'Start Over', CanClose: false }).content;
      popup.OnClose.subscribe(() => {
        window.location.href = '/signup?refresh=1';
      });
      return;
    }

    console.log('ngAfterViewInit', this.Asn);
  }

  public ngOnDestroy(): void {
  }

  public Save(values: any): Observable<TrainingRecord> {
    if (values.License1 && values.License1.State) {
      if (typeof (values.License1.State) === 'object') {
        values.License1.Prefix = values.License1.State['prefix'];
        values.License1.State = values.License1.State['sta_code'];
      }

      if (values.License1.Raw) {
        values.License1.Number = values.License1.Prefix + values.License1.Raw;
      }
    }

    if (values.License2 && values.License2.State) {
      if (typeof (values.License2.State) === 'object') {
        values.License2.Prefix = values.License2.State['prefix'];
        values.License2.State = values.License2.State['sta_code'];
      }

      if (values.License2.Raw) {
        values.License2.Number = values.License2.Prefix + values.License2.Raw;
      }
    }

    const record = new TrainingRecord(values);

    return this.service.Save(record).pipe(tap(result => {
      const value = Object.assign(result);
      value.License1 = Object.assign(result.License1);
      value.License2 = Object.assign(result.License2);
      value.School = Object.assign(result.School);

      const format = 'MM/dd/yyyy';

      if (result.License1.State && typeof (result.License1.State) === 'string') {
        value.License1.State = this.settings.states.find(sta => sta.sta_code === result.License1.State);
      }

      if (result.License1.Expire && result.License1.Expire instanceof Date) {
        value.License1.Expire = formatDate(result.License1.Expire, format, 'en-US');
      }

      if (result.License2.State && typeof (result.License2.State) === 'string') {
        value.License2.State = this.settings.states.find(sta => sta.sta_code === result.License2.State);
      }

      if (result.License2.Expire && result.License2.Expire instanceof Date) {
        value.License2.Expire = formatDate(result.License2.Expire, format, 'en-US');
      }

      if (result.GraduationDate && result.GraduationDate instanceof Date) {
        value.GraduationDate = formatDate(result.GraduationDate, format, 'en-US');
      }

      this.form.reset(value);
    }));
  }

  public GetTraining(): Observable<TrainingRecord> {
    if (this.Asn) {
      if (this.service.Training.has(this.Asn)) {
        let training: ITrainingRecord = this.service.Training.get(this.Asn);

        return this.MergeOrCreateSkeleton(training).pipe(
          map(training => {
            if (empty(training.NameAttended)) {
              training.NameAttended = this.wizard.Individual.ind_full_name_cp;
            }

            return new TrainingRecord(training);
          }),
          tap(training => {
            this.form.reset(training);
          }),
          shareReplay(1),
          delay(0),
        );
      } else {
        return this.service.GetTrainingRecord(this.Asn, (this.SkeletonActions & SkeletonActions.ADD) ? this.Skeleton : null).pipe(
          switchMap(training => {
            if (training) {
              return this.MergeOrCreateSkeleton(training);
            }

            return of(training);
          }),
          map(training => {
            if (training instanceof TrainingRecord) {
              if (empty(training.NameAttended)) {
                training.NameAttended = this.wizard.Individual.ind_full_name_cp;
              }
            }

            return new TrainingRecord(training);
          }),
          tap(training => {
            if (training instanceof TrainingRecord) {
              this.form.reset(training);
            }
          }),
          shareReplay(1),
          delay(0),
        );
      }
    }
  }

  private MergeSkeleton(source: ITrainingRecord): ITrainingRecord {
    const training = { ...source };

    training.School = Object.assign(training.School, this.Skeleton.School);

    if (this.Skeleton.GraduationDate) {
      training.GraduationDate = this.Skeleton.GraduationDate;
    }

    if (this.Skeleton.CourseLength) {
      training.CourseLength = this.Skeleton.CourseLength;
    }

    if (this.Skeleton.NameAttended) {
      training.NameAttended = this.Skeleton.NameAttended;
    }

    if (this.Skeleton.Primary) {
      training.Primary = this.Skeleton.Primary;
    }

    return training;
  }

  private MergeOrCreateSkeleton(source: ITrainingRecord): Observable<TrainingRecord> {
    let training: ITrainingRecord = { ...source };

    if (this.Skeleton && (this.SkeletonActions & /* bitwise and */ SkeletonActions.ADD)) {
      if (training.School.Key == this.Skeleton.School.Key) {
        training = this.MergeSkeleton(training);
      } else {
        training = this.Skeleton;
        if (empty(training.NameAttended)) {
          training.NameAttended = this.wizard.Individual.ind_full_name_cp;
        }
        this.form.reset(training);
        return this.service.Save(this.Skeleton);
      }
    }

    if (empty(training.NameAttended)) {
      training.NameAttended = this.wizard.Individual.ind_full_name_cp;
    }

    return of(training as TrainingRecord);
  }

  public Submit(): void {
    if (this.form.valid) {
      const values = Object.assign(this.form.value);

      this.app.wait();
      this.form.disable();

      this.Save(values).subscribe(result => {
        this.app.wait(false);
        this.form.enable();

        this.Next();
      });
    }
  }

  public CanSkip(training: TrainingRecord): boolean {
    const now = new Date();

    if (this.wizard.Package && (this.wizard.Package[training.Association] || this.membership.Data.Memberships[training.Association])) {
      if (this.membership.Data.Memberships[training.Association]) {
        if (this.membership.Data.Memberships[training.Association].Level === 'Professional') {
          if (this.cart.Data === undefined || this.cart.Data === null || !(this.cart.Data.ExistingMemberships instanceof Array)) {
            return false;
          }

          const cpp = this.cart.Data.ExistingMemberships.filter(MbrAsn(training.Association)).some(CPP);
          const IsInstructorReferral = training.School ? training.School.IsInstructorReferral : false;
          const NoHowHeard = empty(this.wizard.Individual.cst_src_key);
          const exempt = (
            training.License1.State
            && this.service.Exempt.get(training.Association).indexOf(training.License1.State) !== -1) || training.License1.Exempt;
          const valid = (training.License1.Raw && training.License1.State && training.License1.Expire && training.License1.Expire > now);
          const graduation = (training.GraduationDate && training.GraduationDate < now);
          let hotstone = true;

          if (training.Association === 'ABMP') {
            if (this.wizard.Individual.ind_yes_hot_stone_practice_flag_ext !== true) {
              hotstone = false;
            } else if (this.wizard.Individual.ind_yes_hot_stone_practice_flag_ext === true) {
              if (
                this.wizard.Individual.ind_hot_stone_agreement_flag_ext !== true
                || this.wizard.Individual.ind_hot_stone_video_flag_ext !== true) {
                this.wizard.Checklist.add('HotStone');
              }
            }
          }

          return cpp && graduation && (exempt || valid) && hotstone && !(IsInstructorReferral && NoHowHeard);
        } else {
          return false;
        }
      }
    }

    return !!this.wizard.Package;
  }

  public Next(): void {
    if (!this.wizard.Package) {
      const msg = 'Could not find package information. Please start the signup process from the beginning.';
      const popup: AlertModalComponent = this.popup.Open(msg, { CloseText: 'Start Over', CanClose: false }).content;
      popup.OnClose.subscribe(() => {
        window.location.href = '/signup?refresh=1';
      });
      return;
    }

    this.Asn = null;
    this.Title = null;
    this.Logo = null;
    this.Type = null;
    this.PreviousRecord = null;
    this.Selection = null;
    this.group = null;
    this.form = null;
    this.Training$ = null;

    for (const asn of Associations) {
      if (this.wizard.Package[asn]) {
        if (!this.service.Training.has(asn)) {
          this.router.navigate(['../', asn], { relativeTo: this.route });
          return;
        }
      }
    }

    if (this.wizard.Package.p02_key) {
      this.router.navigate(['terms'], { relativeTo: this.route.parent });
    } else {
      this.router.navigate(['confirm'], { relativeTo: this.route.parent });
    }
  }
}
